class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// "Wyczyść koszyk" above the cart page product list: empties the cart after
// a confirmation and reloads the page, which then shows the empty cart.
class CartClearButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    this.button.addEventListener('click', () => {
      if (!window.confirm(this.dataset.confirm)) return;

      this.button.disabled = true;
      document.getElementById('main-cart-items')?.classList.add('cart__items--disabled');

      fetch(`${routes.cart_url}/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } })
        .then(() => window.location.reload())
        .catch((error) => {
          console.error(error);
          this.button.disabled = false;
          document.getElementById('main-cart-items')?.classList.remove('cart__items--disabled');
        });
    });
  }
}

if (!customElements.get('cart-clear-button')) customElements.define('cart-clear-button', CartClearButton);

// Discount code field in the cart page summary (sections/main-cart-footer.liquid).
// Codes are sent to /cart/update.js as the full list of codes, so adding one
// keeps the others and removing one sends the list without it. A code that
// does not lower any price (invalid, already used, not for these products...)
// is taken off again and an error is shown. On success the cart page sections are
// re-rendered, and the applied code shows up as a discount row above the field.
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.cart-discount__form');
    this.input = this.querySelector('.cart-discount__input');
    this.applyButton = this.querySelector('.cart-discount__apply');
    this.error = this.querySelector('.cart-discount__error');

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
    });

    // Remove buttons in the discount rows above the field
    this.removeButtons.forEach((button) =>
      button.addEventListener('click', () => this.remove(button.dataset.discountCode))
    );
  }

  get removeButtons() {
    return (this.closest('.js-contents') || this).querySelectorAll('[data-discount-remove]');
  }

  // Codes that lower the price (the discount rows). A code on the cart that
  // gives no discount is not listed, and is dropped on the next apply/remove.
  get codes() {
    return [...this.removeButtons].map((button) => button.dataset.discountCode);
  }

  async apply() {
    const code = this.input.value.trim();
    if (!code) return;

    const existingCodes = this.codes;
    if (existingCodes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
      this.input.value = '';
      return;
    }

    this.setLoading(true);
    this.showError('');

    try {
      // A code can be reported as applicable and still give nothing (e.g.
      // already used), so it only counts when the cart's discount grows.
      const before = await (await fetch(`${routes.cart_url}.js`)).json();
      const cart = await this.update([...existingCodes, code]);
      const result = cart.discount_codes?.find((discount) => discount.code.toLowerCase() === code.toLowerCase());

      if (!result?.applicable || !(cart.total_discount > before.total_discount)) {
        // Take the rejected code off the cart again, keeping the others.
        await this.update(existingCodes);
        this.showError(this.dataset.errorInvalid);
        this.setLoading(false);
        this.input.focus();
        return;
      }

      await this.render(cart);
    } catch (error) {
      console.error(error);
      this.showError(this.dataset.errorGeneric);
      this.setLoading(false);
    }
  }

  async remove(code) {
    this.setLoading(true);
    // A Google automated discount code would otherwise be re-applied by
    // google-automated-discounts.js on the next cart update.
    window.GoogleAutomatedDiscounts?.forgetCode?.(code);

    try {
      const cart = await this.update(
        this.codes.filter((existing) => existing.toLowerCase() !== code.toLowerCase())
      );
      await this.render(cart);
    } catch (error) {
      console.error(error);
      this.showError(this.dataset.errorGeneric);
      this.setLoading(false);
    }
  }

  async update(codes) {
    const response = await fetch(`${routes.cart_update_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) throw new Error(cart.description || cart.message);
    return cart;
  }

  // Same sections as after a quantity change in CartItems.
  async render(cart) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems) return window.location.reload();

    const sectionsToRender = cartItems.getSectionsToRender();
    const sectionIds = [...new Set(sectionsToRender.map((section) => section.section))];
    const sections = await (await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)).json();

    sectionsToRender.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container || !sections[section.section]) return;
      const target = container.querySelector(section.selector) || container;
      target.innerHTML = cartItems.getSectionInnerHTML(sections[section.section], section.selector);
    });

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
  }

  setLoading(isLoading) {
    this.applyButton.disabled = isLoading;
    this.applyButton.classList.toggle('loading', isLoading);
    this.applyButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
    this.removeButtons.forEach((button) => (button.disabled = isLoading));
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = !message;
  }
}

if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);

// Floating checkout bar on the cart page (sections/main-cart-footer.liquid).
// Phones and tablets only, where the summary sits below the product list; it
// shows while the summary's checkout button is off screen. The total is copied
// from the summary's "Przewidywana suma", so it follows quantity changes.
// Uses the product page's sticky bar classes, including body.sticky-atc-active
// and --sticky-atc-height, which lift the floating widgets above the bar.
class StickyCheckout extends HTMLElement {
  connectedCallback() {
    this.footer = this.closest('#main-cart-footer');
    this.trigger = document.getElementById('checkout');
    this.value = this.querySelector('.sticky-checkout__value');
    if (!this.footer || !this.trigger) return;

    this.viewport = window.matchMedia('(max-width: 989px)');
    this.onViewportChange = () => this.updateVisibility();
    this.viewport.addEventListener('change', this.onViewportChange);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.isTriggerVisible = entry.isIntersecting;
      this.updateVisibility();
    });
    this.intersectionObserver.observe(this.trigger);

    // The summary is re-rendered on cart changes, and the footer gets
    // .is-empty once the last product is removed.
    this.footerObserver = new MutationObserver(() => {
      this.syncTotal();
      this.updateVisibility();
    });
    this.footerObserver.observe(this.footer, { attributes: true, attributeFilter: ['class'] });
    const summary = this.footer.querySelector('.cart__blocks');
    if (summary) this.footerObserver.observe(summary, { childList: true, subtree: true });

    this.resizeObserver = new ResizeObserver(() => this.publishHeight());
    this.resizeObserver.observe(this);

    this.syncTotal();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('change', this.onViewportChange);
    this.intersectionObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.setVisible(false);
  }

  syncTotal() {
    const total = this.footer.querySelector('.cart__estimated-total .totals__total-value')?.textContent.trim();
    if (total && this.value.textContent !== total) this.value.textContent = total;
  }

  updateVisibility() {
    this.setVisible(
      this.viewport.matches && this.isTriggerVisible === false && !this.footer.classList.contains('is-empty')
    );
  }

  setVisible(isVisible) {
    if (this.isVisible === isVisible) return;
    this.isVisible = isVisible;
    this.classList.toggle('sticky-atc--visible', isVisible);
    document.body.classList.toggle('sticky-atc-active', isVisible);
    this.publishHeight();
  }

  publishHeight() {
    document.documentElement.style.setProperty('--sticky-atc-height', `${this.isVisible ? this.offsetHeight : 0}px`);
  }
}

if (!customElements.get('sticky-checkout')) customElements.define('sticky-checkout', StickyCheckout);

const CART_QUANTITY_DEBOUNCE_TIMER = 800;

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // Wait a moment after the last +/- click or edit before updating the
    // cart, so the quantity can be clicked up a few times first. Each line
    // waits on its own, so changing two products quickly keeps both changes.
    this.quantityTimers = new Map();
    this.addEventListener('change', (event) => {
      const input = event.target;
      clearTimeout(this.quantityTimers.get(input));
      this.quantityTimers.set(
        input,
        setTimeout(() => {
          this.quantityTimers.delete(input);
          this.onChange(event);
        }, CART_QUANTITY_DEBOUNCE_TIMER)
      );
    });
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = [
            '.free-shipping-bar',
            'cart-drawer-items',
            '.cart-drawer__footer',
            '.cart-drawer__checkout-value',
          ];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // The new items lose the highlight of the line just added
          document.querySelector('cart-drawer')?.highlightAddedItem?.();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-free-shipping',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      // A section can be listed more than once (one entry per part to update).
      sections: [...new Set(this.getSectionsToRender().map((section) => section.section))],
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    // Dims the whole row while it updates (see component-cart.css)
    this.querySelector(`#CartItem-${line}`)?.classList.add('cart-item--loading');
    this.querySelector(`#CartDrawer-Item-${line}`)?.classList.add('cart-item--loading');

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));

    this.querySelector(`#CartItem-${line}`)?.classList.remove('cart-item--loading');
    this.querySelector(`#CartDrawer-Item-${line}`)?.classList.remove('cart-item--loading');
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
